import { parseLocalCalendarDay } from '../../../utils/dateTimeUtils';
import { formatCost, formatNumber } from '../../../utils/formatUtils';
import {
  inputTokensKey,
  outputTokensKey,
  type CostChartPoint,
  type CostChartViewMode,
} from '../costChartTypes';
import './CostTooltip.css';

interface TooltipPayloadEntry {
  dataKey: string;
  value: number;
  color?: string;
  name?: string;
  payload?: CostChartPoint;
}

export interface CostTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
  locale: string;
  mode: CostChartViewMode;
  methods: string[];
  methodLabels: Record<string, string>;
  runningTotalLabel: string;
  totalLabel: string;
  inputTokensHint: string;
  outputTokensHint: string;
}

function formatTokenPair(
  inputTokens: number,
  outputTokens: number,
  locale: string,
): string {
  return `↓ ${formatNumber(inputTokens, locale)} · ↑ ${formatNumber(outputTokens, locale)}`;
}

export function CostTooltip({
  active,
  payload,
  label,
  locale,
  mode,
  methods,
  methodLabels,
  runningTotalLabel,
  totalLabel,
  inputTokensHint,
  outputTokensHint,
}: CostTooltipProps) {
  if (!active || !payload?.length || !label) return null;

  const formatted =
    mode === 'daily'
      ? parseLocalCalendarDay(label).toLocaleDateString(locale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : (() => {
          const [year, month] = label.split('-');
          return new Date(
            parseInt(year),
            parseInt(month) - 1,
            1,
          ).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
        })();

  const point = payload[0]?.payload;
  const methodEntries = methods
    .map((method) => payload.find((p) => p.dataKey === method))
    .filter(
      (entry): entry is TooltipPayloadEntry => entry != null && entry.value > 0,
    );
  const totalCost = methodEntries.reduce((sum, entry) => sum + entry.value, 0);
  const totalInputTokens =
    typeof point?.inputTokens === 'number' ? point.inputTokens : 0;
  const totalOutputTokens =
    typeof point?.outputTokens === 'number' ? point.outputTokens : 0;
  const totalEntry_running = payload.find((p) => p.dataKey === 'runningTotal');

  return (
    <div className="cost-tooltip">
      <div className="cost-tooltip__label">{formatted}</div>
      {methodEntries.map((entry) => {
        const methodInput = Number(point?.[inputTokensKey(entry.dataKey)] ?? 0);
        const methodOutput = Number(
          point?.[outputTokensKey(entry.dataKey)] ?? 0,
        );
        return (
          <div key={entry.dataKey} className="cost-tooltip__row">
            <span
              className="cost-tooltip__swatch"
              style={{ background: entry.color }}
            />
            <span className="cost-tooltip__muted">
              {methodLabels[entry.dataKey] ?? entry.dataKey}:{' '}
            </span>
            <strong>{formatCost(entry.value, locale)}</strong>
            <span className="cost-tooltip__muted">
              ({formatTokenPair(methodInput, methodOutput, locale)})
            </span>
          </div>
        );
      })}
      {(methodEntries.length > 0 || totalEntry_running != null) && (
        <div className="cost-tooltip__footer">
          {methodEntries.length > 0 && (
            <div>
              <span className="cost-tooltip__muted">{totalLabel}: </span>
              <strong>{formatCost(totalCost, locale)}</strong>
              <span className="cost-tooltip__muted">
                {' '}
                ({formatTokenPair(totalInputTokens, totalOutputTokens, locale)})
              </span>
            </div>
          )}
          {totalEntry_running != null && (
            <div>
              <span className="cost-tooltip__muted">{runningTotalLabel}: </span>
              <strong>{formatCost(totalEntry_running.value, locale)}</strong>
            </div>
          )}
        </div>
      )}
      {methodEntries.length > 0 && (
        <div className="cost-tooltip__legend">
          ↓ {inputTokensHint} · ↑ {outputTokensHint}
        </div>
      )}
    </div>
  );
}
